// src/components/legal/CookiesModal.tsx
// Cookies Policy modal — content can be overridden from Firestore (siteContent/cookiesPolicy).

import { useEffect, useState } from 'react';
import { doc, getDoc }         from 'firebase/firestore';
import { db }                  from '../../lib/firebase';
import { useLegalModalStore }  from '../../stores/useLegalModalStore';
import { LegalModal }          from './LegalModal';

// ── Hardcoded fallback content ────────────────────────────────────────────────
const FALLBACK_HTML = `
<h2>1. What Are Cookies?</h2>
<p>
  Cookies are small text files placed on your device when you visit a website. They are
  widely used to make websites work, or work more efficiently, and to provide information
  to site owners.
</p>
<p>
  Similar technologies include <strong>local storage</strong> and
  <strong>session storage</strong>, which we also use for saving your progress and
  preferences within the browser.
</p>

<hr />

<h2>2. How We Use Cookies</h2>
<p>START uses cookies and similar technologies for the following purposes:</p>

<h3>2.1 Strictly Necessary Cookies</h3>
<p>
  These cookies are essential for the Service to function and cannot be switched off.
  They are usually set in response to actions you take such as logging in or completing
  exercises.
</p>
<ul>
  <li><strong>Authentication tokens</strong> — keep you logged in during your session.</li>
  <li><strong>CSRF protection tokens</strong> — prevent cross-site request forgery.</li>
  <li><strong>Consent preferences</strong> — remember your cookie choices.</li>
</ul>

<h3>2.2 Functional Cookies</h3>
<p>
  These cookies enable enhanced functionality and personalisation, such as:
</p>
<ul>
  <li>Remembering your language preference (English / Georgian).</li>
  <li>Remembering your dark/light theme preference.</li>
  <li>Storing your last-visited book or exercise position.</li>
</ul>

<h3>2.3 Analytics Cookies</h3>
<p>
  We use anonymised analytics (Firebase Analytics / Google Analytics) to understand how
  users interact with the platform. These cookies collect data such as:
</p>
<ul>
  <li>Pages visited and time spent on each page.</li>
  <li>Exercises started, completed, or abandoned.</li>
  <li>Approximate geographic region (country/city level only).</li>
</ul>
<p>
  No personally identifiable information is included in analytics data. You may opt out
  of analytics at any time — see Section 4 below.
</p>

<h3>2.4 Payment Cookies</h3>
<p>
  When you make a payment, Stripe (our payment processor) may set cookies to prevent
  fraud and provide a smooth checkout experience. These are governed by
  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
</p>

<hr />

<h2>3. Third-Party Cookies</h2>
<p>
  Some cookies on our platform are set by third parties. We do not control these cookies
  and recommend reading the relevant third-party privacy policies:
</p>
<ul>
  <li><strong>Google / Firebase</strong> — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
  <li><strong>Stripe</strong> — <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
</ul>

<hr />

<h2>4. Your Cookie Choices</h2>
<p>You can control cookies in several ways:</p>
<ul>
  <li>
    <strong>Browser settings</strong> — Most browsers allow you to refuse cookies or
    delete existing ones. Refer to your browser's help section for instructions.
    Note that refusing necessary cookies may prevent the Service from functioning correctly.
  </li>
  <li>
    <strong>Analytics opt-out</strong> — Install the
    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>
    to prevent your data being used by Google Analytics.
  </li>
  <li>
    <strong>Profile settings</strong> — Within your account, you can adjust analytics
    preferences under Settings → Privacy.
  </li>
</ul>

<hr />

<h2>5. Cookie Duration</h2>
<ul>
  <li><strong>Session cookies</strong> — deleted when you close your browser.</li>
  <li><strong>Persistent cookies</strong> — remain for up to 12 months unless cleared earlier.</li>
  <li><strong>Local/Session storage</strong> — cleared when you log out or clear your browser data.</li>
</ul>

<hr />

<h2>6. Changes to This Policy</h2>
<p>
  We may update this Cookies Policy to reflect changes in technology or legislation.
  The "Last updated" date at the top of this page will indicate when changes were made.
</p>

<hr />

<h2>7. Contact Us</h2>
<p>
  For cookie-related enquiries:<br />
  <a href="mailto:privacy@startlearning.ge">privacy@startlearning.ge</a>
</p>
`;

// ── Component ─────────────────────────────────────────────────────────────────
export function CookiesModal() {
  const openModal = useLegalModalStore((s) => s.openModal);
  const isOpen    = openModal === 'cookies';

  const [htmlContent, setHtmlContent] = useState<string>(FALLBACK_HTML);

  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'cookiesPolicy'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.html) setHtmlContent(data.html as string);
        }
      })
      .catch(() => {/* use fallback */});
  }, []);

  return (
    <LegalModal
      isOpen={isOpen}
      title="Cookies Policy"
      lastUpdated="1 January 2026"
      htmlContent={htmlContent}
    />
  );
}